import clsx from 'clsx';
import { NetworkCheck, SignalWifi4Bar, SignalWifiOff } from '@material-ui/icons';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import _ from 'lodash';
import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { INNER_CELL_VALUE_CLASS } from '../../data-table/DataTableConstants';
import { columnValueGetter } from '../../data-table/DataTableColumns';
import {
    LABEL_CLASS,
    LABEL_ERROR_CLASS,
    LABEL_GREY_CLASS,
    LABEL_INFO_CLASS,
    LABEL_WARNING_CLASS,
    MATCHING_SITES_CONTAINER_CLASS,
    MORE_SITES_ICON_CLASS,
    STATUS_ICON_CLASS,
    STATUS_ICON_CONTAINER_CLASS,
    WIFI_FAIL,
    WIFI_OFF,
    WIFI_STATUS_ICON_CLASS,
    WIFI_STATUS_ICON_CONTAINER_CLASS,
    WIFI_SUCCESS,
} from './AgentStyles';
import {
    AGENT_DISABLED_DUE_TO_LICENSE,
    AGENT_IS_AGENT_OVER_TEST_LIMIT_FIELD,
    AGENT_IS_DYNAMIC_WIRELESS_ENABLED_FIELD,
    AGENT_IS_WIRELESS_ENABLED_FIELD,
    AGENT_LATEST_AGENT_VERSION,
    AGENT_RECOMMENDED_UPDATE,
    AGENT_REQUIRES_UPDATE,
    AGENT_SITES_LIST_FIELD,
    AGENT_STATUS_DISABLED,
    AGENT_STATUS_DOWN,
    AGENT_STATUS_ENABLING,
    AGENT_STATUS_FIELD,
    AGENT_STATUS_RESTART,
    AGENT_STATUS_RUNNING,
    AGENT_STATUS_UPDATE,
    AGENT_TYPE_FIELD,
    AGENT_TYPE_VIRTUAL,
    AGENT_UPDATE_ERROR,
    AGENT_VITALS_CERT_VALID_AFTER,
    AGENT_VITALS_CERT_VALID_BEFORE,
    AGENT_VITALS_TX_BUDGET_MB,
    AGENT_VITALS_TX_BUDGET_RESET_MINUTES,
    AGENT_WIFI_CONNECTED_FIELD,
    AGENT_WIFI_ERRORS_FIELD,
    AGENT_WIFI_SSID_FIELD,
} from './AgentConstants';
import format, {
    DEFAULT_INVALID_VALUE,
    FORMATTER_TYPES,
    percentFormatter,
} from '../../../utilities/FormattingUtilities';

export function renderStatus(row, column) {
    const value = columnValueGetter(row, column);
    const disabledDueToLicense = row[AGENT_DISABLED_DUE_TO_LICENSE];
    const updateError = row[AGENT_UPDATE_ERROR];
    let spanClasses = [];

    let message;
    if (!_.isEmpty(updateError)) {
        message = updateError;
    }

    if (value === AGENT_STATUS_DOWN) {
        spanClasses = [LABEL_CLASS, LABEL_WARNING_CLASS];
    }

    if (value === AGENT_STATUS_UPDATE || value === AGENT_STATUS_RESTART || value === AGENT_STATUS_ENABLING) {
        message = null;
        spanClasses = [LABEL_CLASS, LABEL_INFO_CLASS];
    }

    if (value === AGENT_STATUS_DISABLED) {
        message = null;
        if (disabledDueToLicense) {
            message = t`The nPoint license has been exceeded, this nPoint has been disabled.`;
        }

        spanClasses = [LABEL_CLASS];
    }

    if (!_.isEmpty(message)) {
        return (
            <div className={STATUS_ICON_CONTAINER_CLASS} title={t`${message.replace(/\.+$/, '')}`}>
                <div className={INNER_CELL_VALUE_CLASS}>
                    <span className={spanClasses.join(' ')}>{value}</span>
                </div>
                <div className={STATUS_ICON_CLASS}>
                    <HelpOutlineIcon />
                </div>
            </div>
        );
    }

    return (
        <div className={INNER_CELL_VALUE_CLASS}>
            <span className={spanClasses.join(' ')}>{value}</span>
        </div>
    );
}

export function renderCertTooltip(row) {
    const after = _.get(row, AGENT_VITALS_CERT_VALID_AFTER);
    const before = _.get(row, AGENT_VITALS_CERT_VALID_BEFORE);
    const certValidAfter = Date.parse(after);
    const certValidBefore = Date.parse(before);
    const _30days = 30 * 24 * 60 * 60 * 1000;
    const now = Date.now();
    let tooltip = '';

    if (certValidBefore > 0) {
        const time = format(certValidBefore, { type: FORMATTER_TYPES.DATETIME });
        tooltip = t`The certificate will expire after ${time}`;
    }

    if (certValidAfter > now) {
        const time = format(certValidAfter, { type: FORMATTER_TYPES.DATETIME });
        tooltip = t`The certificate will be valid after ${time}`;
    } else if (certValidBefore > now && certValidBefore - now <= _30days) {
        const time = format(certValidBefore, { type: FORMATTER_TYPES.DATETIME });
        tooltip = t`The certificate will expire after ${time}`;
    } else if (certValidBefore <= now) {
        const time = format(certValidBefore, { type: FORMATTER_TYPES.DATETIME });
        tooltip = t`The certificate expired after ${time}`;
    }

    return tooltip;
}

export function renderCertDate(row, column) {
    const after = _.get(row, AGENT_VITALS_CERT_VALID_AFTER);
    const before = _.get(row, AGENT_VITALS_CERT_VALID_BEFORE);
    const certValidAfter = Date.parse(after);
    const certValidBefore = Date.parse(before);
    const _30days = 30 * 24 * 60 * 60 * 1000;
    const now = Date.now();
    let spanClasses = [];

    if (certValidAfter > now) {
        spanClasses = [LABEL_CLASS, LABEL_GREY_CLASS];
    } else if (certValidBefore > now && certValidBefore - now <= _30days) {
        spanClasses = [LABEL_CLASS, LABEL_WARNING_CLASS];
    } else if (certValidBefore <= now) {
        spanClasses = [LABEL_CLASS, LABEL_ERROR_CLASS];
    }

    const certDate = columnValueGetter(row, column); // cert before date
    return (
        <div className={INNER_CELL_VALUE_CLASS}>
            <span className={spanClasses.join(' ')}>{certDate}</span>
        </div>
    );
}

export function renderVersion(row, column) {
    const requiredUpdateMessage = i18n._(t`nPoint must be updated to ${row[AGENT_LATEST_AGENT_VERSION]}`);
    const recommendedUpdateMessage = i18n._(t`nPoint can be updated to ${row[AGENT_LATEST_AGENT_VERSION]}`);
    const value = columnValueGetter(row, column);
    let rendered = <span>{value}</span>;

    if (row[AGENT_REQUIRES_UPDATE]) {
        rendered = (
            <span title={requiredUpdateMessage} className={`${LABEL_CLASS} ${LABEL_ERROR_CLASS}`}>
                {value}
            </span>
        );
    } else if (row[AGENT_RECOMMENDED_UPDATE]) {
        rendered = (
            <span title={recommendedUpdateMessage} className={`${LABEL_CLASS} ${LABEL_WARNING_CLASS}`}>
                {value}
            </span>
        );
    }

    return <div className={INNER_CELL_VALUE_CLASS}>{rendered}</div>;
}

export function renderTestCount(row, column) {
    const value = columnValueGetter(row, column);
    const overTheLimitText = i18n._(t`nPoint is over the service test limit`);
    let rendered = <span>{value}</span>;

    if (row[AGENT_IS_AGENT_OVER_TEST_LIMIT_FIELD]) {
        rendered = (
            <span title={overTheLimitText} className={clsx(LABEL_CLASS, LABEL_WARNING_CLASS)}>
                {value}
            </span>
        );
    }

    return <div className={INNER_CELL_VALUE_CLASS}>{rendered}</div>;
}

export function renderTxBudget(row, column) {
    const value = columnValueGetter(row, column);
    const formatValue = percentFormatter(value, { maxDecimals: 0, ltComparison: 1.0 });
    const txBudgetMb = _.get(row, AGENT_VITALS_TX_BUDGET_MB);
    const minutesRemaining = _.get(row, AGENT_VITALS_TX_BUDGET_RESET_MINUTES);
    const txBudgetExceededText = i18n._(
        t`nPoint has exceeded the ${txBudgetMb} MB hourly transmit budget.
        All scheduled tests will be canceled for the next ${minutesRemaining} minute(s).`
    );
    const txBudgetResetTimeText = i18n._(t`New nPoint transmit budget in ${minutesRemaining} minute(s)`);
    let rendered = <span title={txBudgetResetTimeText}>{formatValue}</span>;

    if (value >= 100.0) {
        rendered = (
            <span title={txBudgetExceededText} className={clsx(LABEL_CLASS, LABEL_WARNING_CLASS)}>
                {formatValue}
            </span>
        );
    }

    return <div className={INNER_CELL_VALUE_CLASS}>{rendered}</div>;
}

export function renderWifiSSID(row, column, showSSID = true) {
    const notRunningText = i18n._(t`nPoint not Running`);
    const wifiSuccessText = i18n._(t`Wi-Fi Network Connected`);
    const genericWifiErrorText = i18n._(t`Wi-Fi Network Not Connected`);
    const wifiDisabledText = i18n._(t`Wi-Fi Disabled`);
    const wifiMultiModeText = i18n._(t`nPoint in Multiple SSID Testing Mode`);
    const value = columnValueGetter(row, column);
    const renderedValue = value || DEFAULT_INVALID_VALUE;
    const isRunning = row[AGENT_STATUS_FIELD] === AGENT_STATUS_RUNNING;
    const wifiError = row[AGENT_WIFI_ERRORS_FIELD];
    const isVirtualAgentWiFi = row[AGENT_TYPE_FIELD] === AGENT_TYPE_VIRTUAL;

    const basicRendered = <div title={value || ''}>{renderedValue}</div>;
    let rendered = basicRendered;

    let wiFiConfig = {};

    if (value && DEFAULT_INVALID_VALUE !== value) {
        if (isVirtualAgentWiFi) {
            if (!isRunning) {
                wiFiConfig = {
                    classes: [WIFI_OFF],
                    title: notRunningText,
                    icon: <SignalWifi4Bar />,
                };
            } else if (_.isEmpty(row[AGENT_WIFI_SSID_FIELD])) {
                wiFiConfig = {
                    classes: [WIFI_FAIL],
                    title: wifiError || genericWifiErrorText,
                    icon: <SignalWifi4Bar />,
                };
            } else {
                wiFiConfig = {
                    classes: [WIFI_SUCCESS],
                    title: wifiSuccessText,
                    icon: <SignalWifi4Bar />,
                };
            }
        } else if (!row[AGENT_IS_WIRELESS_ENABLED_FIELD] && !row[AGENT_IS_DYNAMIC_WIRELESS_ENABLED_FIELD]) {
            wiFiConfig = {
                classes: [],
                title: wifiDisabledText,
                icon: <SignalWifiOff />,
            };
        } else if (row[AGENT_IS_DYNAMIC_WIRELESS_ENABLED_FIELD]) {
            wiFiConfig = {
                classes: [],
                title: wifiMultiModeText,
                icon: <NetworkCheck />,
            };
        } else if (row[AGENT_WIFI_CONNECTED_FIELD]) {
            wiFiConfig = {
                classes: [WIFI_SUCCESS],
                title: wifiSuccessText,
                icon: <SignalWifi4Bar />,
            };
        } else if (isRunning) {
            wiFiConfig = {
                classes: [WIFI_FAIL],
                title: wifiError || genericWifiErrorText,
                icon: <SignalWifi4Bar />,
            };
        } else {
            wiFiConfig = {
                classes: [WIFI_OFF],
                title: notRunningText,
                icon: <SignalWifi4Bar />,
            };
        }

        rendered = (
            <div className={WIFI_STATUS_ICON_CONTAINER_CLASS} title={wiFiConfig.title}>
                <div className={`${WIFI_STATUS_ICON_CLASS} ${wiFiConfig.classes.join(' ')}`}>{wiFiConfig.icon}</div>
                {showSSID && basicRendered}
                <div>&nbsp;</div>
            </div>
        );
    }

    return <>{rendered}</>;
}

export function renderMatchingSites(row) {
    const otherSitesStart = 1;
    const matchingSites = row[AGENT_SITES_LIST_FIELD];

    if (!matchingSites || matchingSites.length === 0) {
        return <>{DEFAULT_INVALID_VALUE}</>;
    }

    const showIcon = matchingSites.length > otherSitesStart;
    const firstAgent = _.first(matchingSites);
    const otherAgents = showIcon ? _.slice(matchingSites, otherSitesStart, matchingSites.length).join(', ') : null;

    return (
        <div className={MATCHING_SITES_CONTAINER_CLASS}>
            {firstAgent}
            {showIcon && (
                <div className={MORE_SITES_ICON_CLASS} title={otherAgents}>
                    <HelpOutlineIcon />
                </div>
            )}
        </div>
    );
}
